import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { RootState } from "../../store";
import { connect, ConnectedProps } from "react-redux";
import { getGlobalSearch } from "@/store/app/app.actions";
import { useLocation, useNavigate } from "react-router-dom";
import { MediaModel } from "@/database";
import {
  formatTimestampToAgo,
  mediaStatusToTextAndColor,
  secondsToHms,
  useDebounce,
} from "../../utils";
import { Search, SlidersHorizontal } from "lucide-react";
import noImage from "../../assets/icons/svg/no-image.svg";
import { getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { MEDIA_STATUS } from "../../constants";
import { setGlobalSearchList } from "@/store/app/app.slice";
import FailureThumb from "../../assets/icons/svg/failure.svg";
import { truncateText } from "@/utils/utilFunctions/truncateText";

// Custom event used to open the listing-page FilterSheet from the global
// header. Header doesn't import MediaList; MediaList subscribes to this on
// mount. Keeps the cross-component coupling explicit + one-way.
export const OPEN_MEDIA_FILTERS_EVENT = "open-media-filters";

const HeaderSearch = ({
  getGlobalSearch,
  globalSearchList,
  setGlobalSearchList,
}: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const location = useLocation();
  // Show the filter shortcut only on the listing page where it has somewhere
  // to go. Anywhere else the button would be a dead control.
  const showFilterShortcut = /^\/(media|discover|drive)(\/|$)/.test(
    location.pathname
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (debouncedSearchTerm.length > 2) {
      getGlobalSearch({ title: debouncedSearchTerm });
    }
  }, [debouncedSearchTerm, getGlobalSearch]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setDropdownVisible(false);
    setSearchTerm("");
    setGlobalSearchList({ list: [] });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setDropdownVisible(e.target.value.length > 0);
  };

  return (
    <div className="position-relative h-search">
      <div
        className="input-group rounded-pill d-flex align-items-center h-search-input"
        style={{
          // width: isFocused ? '300px' : '40px',
          // backgroundColor: isFocused ? 'white' : 'transparent',
          // border: isFocused ? '1px solid #ced4da' : 'none',
          boxShadow: isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: "40px",
            height: "29px",
            cursor: "pointer",
            backgroundColor: isFocused ? "white" : "transparent",
            borderRadius: "50%",
            transition: "background-color 0.3s ease",
          }}
          onClick={handleFocus}
        >
          <Search size={18} className="iconColor" />
        </div>
        <motion.input
          type="text"
          // className={`form-control rounded-end-pill px-1 border-0 ${!isFocused ? 'd-none' : ''}`}
          className="form-control rounded-end-pill px-1 border-0"
          placeholder="Search across all platforms..."
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleInputChange}
          value={searchTerm}
          initial={{ width: 0 }}
          animate={{ width: isFocused ? "100%" : 0 }}
          transition={{ duration: 0.3 }}
          style={{ backgroundColor: "transparent" }}
        />
        {showFilterShortcut && (
          <>
            <style>{filterShortcutStyles}</style>
            <button
              type="button"
              className="hs-filter-shortcut"
              // mousedown fires before the input's blur, so the toggle survives
              // the focus-handoff race that would otherwise close the pill
              // before the click resolves.
              onMouseDown={(e) => {
                e.preventDefault();
                window.dispatchEvent(
                  new CustomEvent(OPEN_MEDIA_FILTERS_EVENT)
                );
              }}
              aria-label="Open filters"
              title="Filters"
            >
              <SlidersHorizontal size={16} strokeWidth={2.1} />
            </button>
          </>
        )}
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {dropdownVisible &&
          globalSearchList.length > 0 &&
          debouncedSearchTerm.length > 2 && (
            <RenderSearchDropDown
              list={globalSearchList}
              isFocused={isFocused}
              setDropdownVisible={setDropdownVisible}
            />
          )}
      </AnimatePresence>
    </div>
  );
};

const RenderSearchDropDown = ({
  list,
  setDropdownVisible,
}: {
  list: MediaModel[];
  isFocused: boolean;
  setDropdownVisible: (visible: boolean) => void;
}) => {
  const navigateTo = useNavigate();

  const handleMediaClick = (mediaId: string) => {
    setDropdownVisible(false);
    navigateTo(`/media/${mediaId}/details`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`dropdown-menu show mt-2`}
      style={{
        width: "500px",
        maxHeight: "400px",
        overflow: "auto",
        top: "35px",
      }}
    >
      {list &&
        list.length > 0 &&
        list.map((media) => (
          <motion.div
            key={media.id}
            className="d-flex justify-content-start gap-2 p-3 border-bottom pointer"
            whileHover={{ backgroundColor: "#eeeeee", color: "#6f6f6f" }}
            onMouseDown={() => handleMediaClick(media.id)}
          >
            <div className="position-relative">
              <div
                className="rounded ratio ratio-16x9 border bg-secondary pointer"
                style={{ width: "100px", minWidth: "100px" }}
              >
                <img
                  src={
                    media?.meta?.width < media?.meta?.height
                      ? getThumbnailUrlUtil({
                          media,
                          id: media?.images?.portrait,
                          source: media?.source,
                        })
                      : media?.images?.thumb
                      ? getThumbnailUrlUtil({
                          media,
                          id: media?.images?.thumb,
                          source: media?.source,
                        })
                      : noImage
                  }
                  alt={media.title}
                  onError={(e) => {
                    e.currentTarget.src = FailureThumb;
                  }}
                  className="object-fit-contain position-absolute top-50 start-50 translate-middle"
                />
              </div>
              <span
                className="position-absolute bottom-0 end-0 bg-dark text-light px-1 fs-6"
                style={{ bottom: -1, right: -1 }}
              >
                {secondsToHms(media.meta?.duration)}
              </span>
            </div>

            <div
              className="d-flex flex-column gap-1 flex-wrap"
              style={{ maxWidth: "calc(100% - 180px)" }}
            >
              <div className="anchor flex-wrap text-break">
                {truncateText(String(media.title), 50)}
              </div>
              <small className="text-muted">{`${formatTimestampToAgo(
                media?.ct || 0
              )} |  ${media.id}`}</small>
            </div>
            <div className="ms-auto">
              {media?.media_status === MEDIA_STATUS.MEDIA_STATUS_DRAFT ||
              media?.media_status === MEDIA_STATUS.MEDIA_STATUS_PUBLISHING ? (
                <span
                  className={`badge ${
                    mediaStatusToTextAndColor(media?.media_status || 0)
                      .statusColor
                  }`}
                >
                  {
                    mediaStatusToTextAndColor(media?.media_status || 0)
                      .statusText
                  }
                </span>
              ) : media?.media_status === MEDIA_STATUS.MEDIA_STATUS_INACTIVE ||
                media?.media_status === MEDIA_STATUS.MEDIA_STATUS_PUBLISHED ? (
                <>
                  <span
                    className={`badge ${
                      mediaStatusToTextAndColor(media?.media_status || 0)
                        .statusColor
                    }`}
                  >
                    {
                      mediaStatusToTextAndColor(media?.media_status || 0)
                        .statusText
                    }
                  </span>
                </>
              ) : (
                <span
                  className={`badge ${
                    mediaStatusToTextAndColor(media?.media_status || 0)
                      .statusColor
                  }`}
                >
                  {
                    mediaStatusToTextAndColor(media?.media_status || 0)
                      .statusText
                  }
                </span>
              )}
            </div>
          </motion.div>
        ))}
    </motion.div>
  );
};

const filterShortcutStyles = `
.hs-filter-shortcut {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 4px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #525966;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 120ms ease, color 120ms ease;
}
.hs-filter-shortcut:hover {
  background: rgba(0, 122, 255, 0.10);
  color: #007aff;
}
.hs-filter-shortcut:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.20);
  color: #007aff;
}
`;

const mapState = (state: RootState) => ({
  globalSearchList: state.app.globalSearchResultList,
});
const mapDispatch = {
  getGlobalSearch,
  setGlobalSearchList,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(HeaderSearch);
